import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, runTransaction, serverTimestamp, addDoc, collection } from 'firebase/firestore';

export async function addUserBuyListItem({ uid, hit, deltaQty = 1 }) {
  if (!uid) throw new Error('Not signed in');

  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(userRef, { selectedList: 'default', lists: { default: { listName: 'default', lastUpdated: Date.now() } } }, { merge: true });
  }
  const user = (await getDoc(userRef)).data() || {};
  const selectedList = user.selectedList || 'default';
  if (!selectedList) throw new Error('Select a list first');

  const objectID = hit?.objectID || hit?.objectId || hit?.id;
  if (!objectID) throw new Error('Missing objectID');

  const item = hit.data ?? hit;
  const prices = extractPrices(item);

  // Store imgURL as a single comma-separated string (UI will split when reading)
  let imgArr = [];
  if (Array.isArray(item.imgURL)) {
    imgArr = item.imgURL;
  } else if (typeof item.imgURL === 'string') {
    if (item.imgURL.includes(',')) {
      imgArr = item.imgURL.split(',').map((s) => s.trim()).filter(Boolean);
    } else if (item.imgURL.trim()) {
      imgArr = [item.imgURL.trim()];
    }
  }
  const imgURL = imgArr.join(',');

  const destRef = doc(db, 'buyLists', uid, selectedList, String(objectID));

  await runTransaction(db, async (tx) => {
    const curSnap = await tx.get(destRef);

    if (curSnap.exists()) {
      const cur = curSnap.data() || {};
      const nextTotal = Math.max(0, Number(cur.total || 0) + Number(deltaQty || 0));
      if (nextTotal === 0) { tx.delete(destRef); return; }
      tx.update(destRef, {
        total: nextTotal,
        discount: Number(item.discount ?? 0) || 0,
        prices,
        lastModifiedAt: serverTimestamp(),
      });
    } else {
      const base = {
        code: item.code ?? '',
        brand: item.brand ?? '',
        material: item.material ?? '',
        imgURL,
        weight: item.weight ?? item.weightMetal ?? '0',
        weightCeramic: item.weightCeramic ?? '0',
        discount: Number(item.discount ?? 0) || 0,
        total: Math.max(0, Number(deltaQty || 0)),
        prices,
        createdAt: serverTimestamp(),
        lastModifiedAt: serverTimestamp(),
      };
      if (base.total === 0) return;
      tx.set(destRef, base);
    }

    const lists = user.lists || {};
    const patch = { ...(lists[selectedList] || {}), listName: selectedList, lastUpdated: Date.now() };
    tx.set(userRef, { lists: { ...lists, [selectedList]: patch } }, { merge: true });
  });

  return { success: true };
}

// Boards-specific: stores items under buyListsBoards and users/{uid}.listsBoards
export async function addUserBuyListItemBoard({ uid, hit, deltaQty = 1 }) {
  if (!uid) throw new Error('Not signed in');

  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(
      userRef,
      { selectedListBoard: 'default', listsBoards: { default: { listName: 'default', lastUpdated: Date.now() } } },
      { merge: true }
    );
  }
  const user = (await getDoc(userRef)).data() || {};
  const selectedList = user.selectedListBoard || 'default';
  if (!selectedList) throw new Error('Select a list first');

  const objectID = hit?.objectID || hit?.objectId || hit?.id;
  if (!objectID) throw new Error('Missing objectID');

  const item = hit.data ?? hit;
  const prices = extractPrices(item);

  // Normalize image URL(s) for both SD and HD
  const toCsv = (val) => {
    try {
      if (Array.isArray(val)) return val.join(',');
      if (typeof val === 'string') {
        if (!val) return '';
        if (val.includes(',')) return val.split(',').map((s) => s.trim()).filter(Boolean).join(',');
        return val.trim();
      }
    } catch {}
    return '';
  };
  const imgURL = toCsv(item.imgURL);
  const imgHDURL = toCsv(item.imgHDURL || item.imgURLHD);

  const destRef = doc(db, 'buyListsBoards', uid, selectedList, String(objectID));

  await runTransaction(db, async (tx) => {
    const curSnap = await tx.get(destRef);

    if (curSnap.exists()) {
      const cur = curSnap.data() || {};
      const nextTotal = Math.max(0, Number(cur.total || 0) + Number(deltaQty || 0));
      if (nextTotal === 0) { tx.delete(destRef); return; }
      tx.update(destRef, {
        total: nextTotal,
        discount: Number(item.discount ?? 0) || 0,
        prices,
        lastModifiedAt: serverTimestamp(),
      });
    } else {
      const base = {
        // Boards-only minimal schema
        category: item.category ?? '',
        code: item.code ?? '',
        imgHDURL,
        imgURL,
        discount: Number(item.discount ?? 0) || 0,
        prices,
        total: Math.max(0, Number(deltaQty || 0)),
        createdAt: serverTimestamp(),
        lastModifiedAt: serverTimestamp(),
      };
      if (base.total === 0) return;
      tx.set(destRef, base);
    }

    // Ensure the destination list exists under users/{uid}.listsBoards and update lastUpdated
    const lists = user.listsBoards || {};
    const patch = { ...(lists[selectedList] || {}), listName: selectedList, lastUpdated: Date.now() };
    tx.set(userRef, { listsBoards: { ...lists, [selectedList]: patch } }, { merge: true });
  });

  return { success: true };
}

function extractPrices(src) {
  const from = src?.prices ?? src ?? {};
  const keys = [
    'priceUSD','priceEUR','priceGBP','priceAUD','priceCAD','priceJPY','priceAED','priceRUB',
    'priceCNY','priceKRW','priceINR','priceZAR','priceBRL','priceMXN','priceMYR','priceSEK',
    'priceDKK','priceNOK','priceCHF','pricePLN','priceCZK','priceBGN','priceHRK','priceTRY',
    'priceRON','priceSAR','priceKWD','priceJOD','priceEGP','priceMAD','priceDZD','priceTND',
    'priceETB','priceUZS'
  ];
  const out = {};
  for (const k of keys) {
    const v = Number(from[k]);
    if (Number.isFinite(v)) out[k] = v;
  }
  return out;
}

/**
 * Add a custom CALCULATOR item to the user's currently selected list.
 * Expects a fully prepared `item` object that already contains:
 *   - material: 'CALCULATOR'
 *   - code/brand/imgURL
 *   - weight/weightCeramic/moisture/shippingCost (optional)
 *   - prices: { priceUSD, priceEUR, ... }
 */
export async function addUserBuyListItemCalc({ uid, item, total = 1 }) {
  if (!uid) throw new Error('Not signed in');

  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(
      userRef,
      { selectedList: 'default', lists: { default: { listName: 'default', lastUpdated: Date.now() } } },
      { merge: true }
    );
  }
  const user = (await getDoc(userRef)).data() || {};
  const selectedList = user.selectedList || 'default';
  if (!selectedList) throw new Error('Select a list first');

  const collRef = collection(db, 'buyLists', uid, selectedList);
  const payload = {
    code: 'CALCULATOR',
    brand: 'CALCULATOR',
    material: 'CALCULATOR',
    imgURL: item.imgURL || '',
    // weight kept as string or number per app usage; keep 0 default
    weight: item.weight ?? '0',
    weightCeramic: item.weightCeramic ?? '0',
    // PPM as strings if provided
    pt: typeof item.pt !== 'undefined' ? String(item.pt) : undefined,
    pd: typeof item.pd !== 'undefined' ? String(item.pd) : undefined,
    rh: typeof item.rh !== 'undefined' ? String(item.rh) : undefined,
    // Moisture as string if provided
    moisture: typeof item.moisture !== 'undefined' ? String(item.moisture) : undefined,
    // shippingCost: '' when zero; otherwise pass number
    shippingCost: (Number(item.shippingCost) || 0) === 0 ? '' : Number(item.shippingCost),
    prices: item.prices || {},
    // Do NOT set total for CALCULATOR items (app treats missing as 1)
    createdAt: serverTimestamp(),
    lastModifiedAt: serverTimestamp(),
  };

  // If some other material sneaks in, keep legacy behavior for total
  if (payload.material !== 'CALCULATOR') {
    payload.total = Math.max(0, Number(total || 1));
  }

  await addDoc(collRef, payload);

  const lists = user.lists || {};
  const patch = { ...(lists[selectedList] || {}), listName: selectedList, lastUpdated: Date.now() };
  await setDoc(userRef, { lists: { ...lists, [selectedList]: patch } }, { merge: true });

  return { success: true };
}
